// src/context/webDataContext.js

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { getDatabase, ref, onValue, set, push, remove, get } from "firebase/database";
import { toast } from "react-toastify";
import { app } from "../config/firebaseConfig";

// firebaseUpload is your helper function that uploads a file and returns its download URL

// Create the context
const WebDataContext = createContext();

// Provider component
export const WebDataProvider = ({ children }) => {
  const [websiteData, setWebsiteData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Cache state for different data types
  const [navbarData, setNavbarData] = useState(null);
  const [footerData, setFooterData] = useState(null);
  const [blogData, setBlogData] = useState(null);
  const [policiesData, setPoliciesData] = useState(null);
  const [contactData, setContactData] = useState(null);
  const [contactMessagesData, setContactMessagesData] = useState(null);

  const db = getDatabase(app);

  // 1. Real-time listener on the entire "website" node
  useEffect(() => {
    const websiteRef = ref(db, "website");
    const unsubscribe = onValue(
      websiteRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          setWebsiteData(data);
          
          // Cache specific data sections
          if (data.navbar) setNavbarData(data.navbar);
          if (data.footer) setFooterData(data.footer);
          if (data.blogs) setBlogData(Object.values(data.blogs));
          if (data.policies) setPoliciesData(Object.entries(data.policies).map(([id, data]) => ({ id, ...data })));
          if (data.contact?.content) setContactData(data.contact.content);
          if (data.contact?.messages) setContactMessagesData(
            Object.entries(data.contact.messages).map(([id, data]) => ({ id, ...data }))
          );
        } else {
          // If "website" node doesn't exist yet, set an empty object
          setWebsiteData({});
        }
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching website data:", err);
        setError(err);
        setLoading(false);
      }
    );

    // Cleanup listener on unmount
    return () => unsubscribe();
  }, [db]);

  // Home page data getter with caching
  const getHomePageData = useCallback(() => {
    return websiteData?.home_page || {};
  }, [websiteData?.home_page]);

  // Featured in data getter with caching
  const getFeaturedInData = useCallback(() => {
    return websiteData?.featured_in || {};
  }, [websiteData?.featured_in]);

  // About us data getter with caching
  const getAboutUsData = useCallback(() => {
    return websiteData?.about_us || {};
  }, [websiteData?.about_us]);

  // Services album data getter with caching
  const getServicesAlbumData = useCallback(() => {
    return websiteData?.services_album || {};
  }, [websiteData?.services_album]);

  // Service provider join data getter with caching
  const getServiceProviderJoinData = useCallback(() => {
    return websiteData?.service_provider_join || {};
  }, [websiteData?.service_provider_join]);

  // How it works data getter with caching
  const getHowItWorksData = useCallback(() => {
    return websiteData?.how_it_works || {};
  }, [websiteData?.how_it_works]);

    const getGetStartedData = useCallback(() => {
    return websiteData?.get_started || {};
  }, [websiteData?.get_started]);


  // Why choose us data getter with caching
  const getWhyChooseUsData = useCallback(() => {
    return websiteData?.why_choose_us || {};
  }, [websiteData?.why_choose_us]);

  // Testimonials data getter with caching
  const getTestimonials = useCallback(() => {
    return websiteData?.testimonials ? Object.values(websiteData.testimonials) : [];
  }, [websiteData?.testimonials]);

  // FAQs data getter with caching
  const getFAQsData = useCallback(() => {
    return websiteData?.faqs || { mainTitle: "Frequently Asked Questions", items: [] };
  }, [websiteData?.faqs]);

  // Blog data getter with caching
  const getBlogData = useCallback(() => {
    return blogData || (websiteData?.blogs ? Object.values(websiteData.blogs) : []);
  }, [blogData, websiteData?.blogs]);

  // Contact page data getter with caching
  const getContactPageData = useCallback(() => {
    return contactData || websiteData?.contact?.content || {
      contactHeading: "Contact Us",
      contactSubHeading: "How Can We Help You?",
      contactInfo: {
        phone: "+91 9019102917",
        email: "maideazesolutions@gmail.com",
        address:
          "Auraiya, Uttar Pradesh - 206122",
      },
    };
  }, [contactData, websiteData?.contact?.content]);

  // Contact messages data getter with caching
  const getContactData = useCallback(() => {
    return contactMessagesData || (websiteData?.contact?.messages
      ? Object.entries(websiteData.contact.messages).map(([id, data]) => ({ id, ...data }))
      : []);
  }, [contactMessagesData, websiteData?.contact?.messages]);

  // Post a new contact message under "website/contact/messages"
  const postContactData = async (contactData) => {
    try {
      const messagesRef = ref(db, "website/contact/messages");
      const newMessageRef = push(messagesRef);
      await set(newMessageRef, { ...contactData, date: new Date().toISOString() });
      
      // Update local cache
      const newMessage = { id: newMessageRef.key, ...contactData, date: new Date().toISOString() };
      setContactMessagesData(prev => prev ? [...prev, newMessage] : [newMessage]);
    } catch (err) {
      console.error("Error posting contact data:", err);
      setError(err);
      toast.error("Failed to submit contact message.");
    }
  };

  // Admin-side: Update an existing contact message
  const updateContactData = async (contactId, newData) => {
    try {
      await set(ref(db, `website/contact/messages/${contactId}`), newData);
      
      // Update local cache
      setContactMessagesData(prev => 
        prev ? prev.map(item => item.id === contactId ? { ...item, ...newData } : item) : prev
      );
    } catch (err) {
      console.error("Error updating contact data:", err);
      setError(err);
      toast.error("Failed to update contact message.");
    }
  };

  // Admin-side: Delete a contact message
  const deleteContactData = async (contactId) => {
    try {
      await remove(ref(db, `website/contact/messages/${contactId}`));
      
      // Update local cache
      setContactMessagesData(prev => 
        prev ? prev.filter(item => item.id !== contactId) : prev
      );
    } catch (err) {
      console.error("Error deleting contact data:", err);
      setError(err);
      toast.error("Failed to delete contact message.");
    }
  };

  // Admin-side: Update the static contact page content stored at "website/contact/content"
  const updateContactPageData = async (newData) => {
    try {
      const updatedWebsiteData = {
        ...websiteData,
        contact: {
          ...websiteData?.contact,
          content: { ...websiteData?.contact?.content, ...newData },
        },
      };
      await set(ref(db, "website"), updatedWebsiteData);
      
      // Update local cache
      setContactData(prev => ({ ...prev, ...newData }));
    } catch (err) {
      console.error("Error updating contact page data:", err);
      setError(err);
      toast.error("Failed to update contact page content.");
    }
  };

  // Policies data getter with caching
  const getPoliciesData = useCallback(() => {
    return policiesData || (websiteData?.policies
      ? Object.entries(websiteData.policies).map(([id, data]) => ({ id, ...data }))
      : []);
  }, [policiesData, websiteData?.policies]);

  // Add a new policy
  const addPolicy = async (policyData) => {
    try {
      const policiesRef = ref(db, "website/policies");
      const newPolicyRef = push(policiesRef);
      await set(newPolicyRef, policyData);
      
      // Update local cache
      const newPolicy = { id: newPolicyRef.key, ...policyData };
      setPoliciesData(prev => prev ? [...prev, newPolicy] : [newPolicy]);
    } catch (err) {
      console.error("Error adding policy:", err);
      setError(err);
      toast.error("Failed to add policy.");
    }
  };

  // Update an existing policy
  const updatePolicy = async (policyId, policyData) => {
    try {
      await set(ref(db, `website/policies/${policyId}`), policyData);
      
      // Update local cache
      setPoliciesData(prev => 
        prev ? prev.map(item => item.id === policyId ? { ...item, ...policyData } : item) : prev
      );
    } catch (err) {
      console.error("Error updating policy:", err);
      setError(err);
      toast.error("Failed to update policy.");
    }
  };

  // Delete a policy
  const deletePolicy = async (policyId) => {
    try {
      await remove(ref(db, `website/policies/${policyId}`));
      
      // Update local cache
      setPoliciesData(prev => 
        prev ? prev.filter(item => item.id !== policyId) : prev
      );
    } catch (err) {
      console.error("Error deleting policy:", err);
      setError(err);
      toast.error("Failed to delete policy.");
    }
  };

  // Navbar data getter with caching
  const getNavbarData = useCallback(async () => {
    if (navbarData) return navbarData;
    
    try {
      const navbarRef = ref(db, "website/navbar");
      const snapshot = await get(navbarRef);
      if (snapshot.exists()) {
        const data = snapshot.val();
        setNavbarData(data);
        return data;
      }
      return {};
    } catch (err) {
      console.error("Error fetching navbar data:", err);
      toast.error("Failed to fetch navbar data.");
      return {};
    }
  }, [db, navbarData]);

  // Footer data getter with caching
  const getFooterData = useCallback(async () => {
    if (footerData) return footerData;
    
    try {
      const footerRef = ref(db, "website/footer");
      const snapshot = await get(footerRef);
      if (snapshot.exists()) {
        const data = snapshot.val();
        setFooterData(data);
        return data;
      }
      return {};
    } catch (err) {
      console.error("Error fetching footer data:", err);
      toast.error("Failed to fetch footer data.");
      return {};
    }
  }, [db, footerData]);

  return (
    <WebDataContext.Provider
      value={{
        websiteData,
        loading,
        error,
        getHomePageData,     
        getFeaturedInData,
        getAboutUsData,
        getServicesAlbumData,
        getServiceProviderJoinData,
        getHowItWorksData,
        getGetStartedData,
        getWhyChooseUsData,
        getTestimonials,
        getFAQsData,
        getBlogData,
        getContactPageData,
        postContactData,
        getContactData,
        updateContactData,
        deleteContactData,
        updateContactPageData,
        getPoliciesData,
        addPolicy,
        updatePolicy,
        deletePolicy,
        getNavbarData,
        getFooterData,
      }}
    >
      {children}
    </WebDataContext.Provider>
  );
};

// Custom hook to use this context
export const useWebData = () => useContext(WebDataContext);
